export type TextDirection = 'ltr' | 'rtl';

export interface LocaleMetadata {
  localeForTranslations: string;
  localeForFormatting: string;
  direction: TextDirection;
}

export enum SenderErrorState {
  INELIGIBLE = 'INELIGIBLE',
  LIMIT_HIT = 'LIMIT_HIT',
}

export enum ReceiverErrorState {
  INVALID_CODE = 'INVALID_CODE',
  SENDER_INELIGIBLE = 'SENDER_INELIGIBLE',
  SENDER_LIMIT_HIT = 'SENDER_LIMIT_HIT',
  ALREADY_PREMIUM = 'ALREADY_PREMIUM',
  NOT_INTRO_ELIGIBLE = 'NOT_INTRO_ELIGIBLE',
  STUDENT_NOT_VERIFIED = 'STUDENT_NOT_VERIFIED',
  COUNTRY_INVALID = 'COUNTRY_INVALID',
}

export interface Benefit {
  iconName: string;
  title: string;
  subtitle: string;
}

export interface Benefits {
  header: string;
  items: Benefit[];
}

export interface LibraryImporterModule {
  title: string;
  subtitle: string;
  imageUrl: string;
}

export interface ReferralExperiment {
  experimentName: string;
  experimentArm: string;
}

export interface ReceiverLandingPageContent {
  hero: Hero;
  benefits: Benefits;
  libraryImporterModule: LibraryImporterModule | null;
}

export interface ErrorPageContent {
  title: string;
  subtitle: string;
  cta?: Cta;
}

export type ConsentState =
  | { kind: 'checkbox'; text: string; errorText: string }
  | { kind: 'notice'; text: string }
  | { kind: 'none' };

export type HeroImageLayout = 'gradient' | 'full_bleed';

export interface HeroImage {
  url: string;
  layout: HeroImageLayout;
}

export interface Cta {
  ctaLabel: string;
  ctaUri?: string;
}

export interface Hero {
  title: string;
  subtitle: string;
  legalDisclaimer?: string;
  cta?: Cta;
  image?: HeroImage;
  consent: ConsentState;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface Faq {
  title: string;
  items: FaqItem[];
}

export interface SenderPageContent {
  hero: Hero;
  faq: Faq;
}

export interface PageMetadata {
  pageTitle: string;
  pageDescription: string;
}

export type SenderResult = SenderSuccess | SenderError;

export interface SenderSuccess {
  kind: 'success';
  locale: LocaleMetadata;
  country: string;
  pageContent: SenderPageContent;
  shareCardUrl: string;
  shareUrl: string;
  referralCode: string;
  shareCardMessage: string;
  pageMetadata: PageMetadata | null;
}

export interface SenderError {
  kind: 'error';
  locale: LocaleMetadata;
  errorState: SenderErrorState;
  country: string;
  pageContent: ErrorPageContent;
}

export enum ReceiverLandingPageErrorState {
  COUNTRY_INELIGIBLE = 'COUNTRY_INELIGIBLE',
}

export type ReceiverLandingResult =
  | ReceiverLandingSuccess
  | ReceiverLandingError;

export interface ReceiverLandingSuccess {
  kind: 'success';
  locale: LocaleMetadata;
  country: string;
  pageContent: ReceiverLandingPageContent;
  ogImage: string;
  senderUserId?: string;
  pageMetadata: PageMetadata | null;
  activeExperiments: ReferralExperiment[];
}

export interface ReceiverLandingError {
  kind: 'error';
  locale: LocaleMetadata;
  errorState: ReceiverLandingPageErrorState;
  country: string;
  pageContent: ErrorPageContent;
  senderUserId?: string;
  activeExperiments: ReferralExperiment[];
}

export type ReceiverRedirectResult =
  | ReceiverRedirectSuccess
  | ReceiverRedirectError;

export interface ReceiverRedirectSuccess {
  kind: 'success';
  redirectUrl: string;
}

export interface ReceiverRedirectError {
  kind: 'error';
  errorState: ReceiverErrorState;
  country: string;
  pageContent: ErrorPageContent;
  senderUserId?: string;
}
